import { IDisableRedirectOptions } from '@kurtosys/types/helpers/redirect/IDisableRedirectOptions.js';
import { IRedirect } from '@kurtosys/types/helpers/redirect/IRedirect.js';
import { IRedirectConditional } from '@kurtosys/types/helpers/redirect/IRedirectConditional.js';
import { IRedirectConstructorOptions } from '@kurtosys/types/helpers/redirect/IRedirectConstructorOptions.js';
import { IRedirectContext } from '@kurtosys/types/helpers/redirect/IRedirectContext.js';
import { IRedirectOptions } from '@kurtosys/types/helpers/redirect/IRedirectOptions.js';
import { IRedirectParamCase } from '@kurtosys/types/helpers/redirect/IRedirectParamCase.js';
import { TRedirectHelperDependencies } from '@kurtosys/types/helpers/TRedirectHelperDependencies.js';
import { paramCase } from 'change-case';

import { filterOutEmpty } from '../commonUtils/collection.js';
import { isArrayLike } from '../commonUtils/isArrayLike.js';
import { replacePlaceholders } from '../commonUtils/replacePlaceholders.js';
import { isNullOrEmpty } from '../commonUtils/typeChecks.js';

import { ConditionalHelper } from './ConditionalHelper.js';
import { TranslationHelper } from './TranslationHelper.js';

export class RedirectHelper {
	public dependencies: TRedirectHelperDependencies;
	public url: URL;
	private translationHelper: TranslationHelper;
	private redirectContext: IRedirectContext = {};
	private originPathPlaceholder = false;
	private originPath = '';
	private shouldCompareUserURL = false;

	constructor(
		dependencies: TRedirectHelperDependencies,
		url: string,
		base?: string,
		options?: IRedirectConstructorOptions,
	) {
		this.dependencies = dependencies;
		if (!base) {
			base = window && window.location.origin;
		}
		this.url = new URL(url, base);
		if (options) {
			if (options.shouldCompareUserURL) {
				this.shouldCompareUserURL = options.shouldCompareUserURL;
			}
			if (options.translationHelper) {
				this.translationHelper = options.translationHelper;
			}
		}
	}

	/**
	 * Should be deprecated in future, application and/or component should rather get the url based on redirect logic via getRedirectPath
	 * then call the redirect method with the url and the options.
	 * This is for the apps needing the redirect url to log to the data layer
	 * @param redirect
	 * @param inputs
	 */
	go(redirect: IRedirect, inputs?: object): void {
		const { options } = redirect;
		const redirectPath = this.getRedirectPath(redirect, inputs);
		const { disableRedirectOptions } = options;

		if (redirectPath && (!this.shouldCompareUserURL || document.location.href !== redirectPath)) {
			// Check if redirection should be prevented based on disableRedirectOptions
			if (
				disableRedirectOptions &&
				this.shouldNotRedirect(document.location.href, redirectPath, disableRedirectOptions)
			) {
				// Do not perform redirection if conditions are met
				return;
			}

			if (options && options.target) {
				window.open(redirectPath, options.target);
			} else if (redirectPath === document.location.href) {
				window.location.reload();
			} else {
				document.location.href = redirectPath;
			}
		}
	}

	/**
	 * Checks whether two URLs should not be redirected based on the provided options.
	 *
	 * @param url1 - The first URL.
	 * @param url2 - The second URL.
	 * @param disableRedirectOptions - An object containing options for disabling redirects.
	 * @returns True if the URLs should not be redirected, otherwise false.
	 */
	shouldNotRedirect(url1: string, url2: string, disableRedirectOptions: IDisableRedirectOptions): boolean {
		const { validateWithoutQueryString } = disableRedirectOptions;

		/**
		 * Parses the query string of a URL and returns a set of query string parameters.
		 *
		 * @param url - The URL to parse.
		 * @returns A set containing the query string parameters.
		 */
		const parseQueryString = (url: string) => {
			const queryString = new URL(url).search;
			const params = new URLSearchParams(queryString);
			const queryStringParams = new Set(params.toString().split('&'));
			return queryStringParams;
		};

		const queryStringParams1 = parseQueryString(url1);
		const queryStringParams2 = parseQueryString(url2);

		if (
			validateWithoutQueryString &&
			new URL(url1).origin + new URL(url1).pathname === new URL(url2).origin + new URL(url2).pathname
		) {
			return true;
		}

		if (
			new URL(url1).origin + new URL(url1).pathname === new URL(url2).origin + new URL(url2).pathname &&
			url1.split('?')[0] === url2.split('?')[0] &&
			queryStringParams1.size === queryStringParams2.size &&
			[...queryStringParams1].every((param) => queryStringParams2.has(param))
		) {
			return true;
		}

		return false;
	}

	getRedirectPath(redirect: IRedirect | undefined, inputs?: object): string {
		if (redirect) {
			const { conditionalRedirects, defaultUrl, base, options = {} } = redirect;
			let validRedirect: IRedirectConditional | undefined;
			if (conditionalRedirects) {
				validRedirect = conditionalRedirects.find((conditionalRedirect): boolean => {
					const { conditional } = conditionalRedirect;
					const conditionalHelper = new ConditionalHelper(this.dependencies, conditional);
					return conditionalHelper.matches(inputs);
				});
			}
			this.originPath = this.url.pathname;
			const redirectOptions = (validRedirect && validRedirect.options) || options;
			if (redirectOptions.originPathTransforms && this.dependencies?.transformClass) {
				for (const transformOptions of redirectOptions.originPathTransforms) {
					const transform = new this.dependencies.transformClass(
						this.dependencies,
						transformOptions,
						this.translationHelper,
					);
					this.originPath = transform.execute(this.originPath, { queryOptionsType: 'none' }, {});
				}
			}
			const url = (validRedirect && validRedirect.url) || defaultUrl;
			this.initializeContext(redirectOptions, inputs);
			const replacedUrl = this.replacePlaceholders(url);
			return this.getRedirectUrl(replacedUrl, base, null, null, false);
		}
	}

	getRedirectUrl(
		redirectString: string,
		redirectBase: string,
		options: IRedirectOptions,
		inputs?: object,
		initializeContext = true,
	): string {
		if (initializeContext) {
			this.initializeContext(options, inputs);
		}
		const base = this.redirectOptions.preserveOrigin ? this.url.origin : redirectBase;
		const redirectUrl = new URL(redirectString, base || (window && window.location.origin));
		this.updatePathname(redirectUrl);
		this.updateSearchQueryString(redirectUrl);
		this.updateHash(redirectUrl);
		return redirectUrl.toString();
	}

	private initializeContext(redirectOptions: IRedirectOptions = {}, inputs: object = {}): void {
		this.redirectContext.options = redirectOptions;
		this.inputs = inputs;
	}

	private get redirectOptions(): IRedirectOptions {
		return this.redirectContext.options || {};
	}

	private get translatePlaceholderValues(): boolean {
		return (
			this.translationHelper &&
			this.redirectContext.options &&
			this.redirectContext.options.translatePlaceholderValues
		);
	}

	private set inputs(inputs: object) {
		this.redirectContext.inputs = inputs || {};
		const inputsTranslated = { ...inputs };
		if (this.translatePlaceholderValues) {
			Object.keys(inputsTranslated).forEach((inputKey) => {
				inputsTranslated[inputKey] = this.translationHelper.translate(inputsTranslated[inputKey]);
			});
			this.redirectContext.inputsTranslated = inputsTranslated;
		}
	}

	private get inputs(): object {
		if (this.translatePlaceholderValues && this.hasTranslatedInputs) {
			return this.redirectContext.inputsTranslated;
		}
		return this.redirectContext.inputs || {};
	}

	private get hasInputs(): boolean {
		return this.redirectContext.inputs && Object.keys(this.redirectContext.inputs).length > 0;
	}

	private get hasTranslatedInputs(): boolean {
		return this.redirectContext.inputsTranslated && Object.keys(this.redirectContext.inputsTranslated).length > 0;
	}

	private updatePathname(redirectUrl: URL): void {
		const redirectPathSegments = [];
		const paramCaseOptions: IRedirectParamCase = Object.assign(
			{ apply: false, separator: '-' },
			this.redirectOptions.paramCase,
		);
		let shouldAddTrailingSlash = this.redirectOptions.forceTrailingSlash || false;

		const currentUrlSegments = this.originPath.split('/');
		const redirectUrlSegements = redirectUrl.pathname.split('/');

		if (this.redirectOptions.preserveOriginPathWithOverrides) {
			// We need to ensure that we are counting the segments and not the slashes
			const currentPath = this.originPath.replace(/^\/|\/$/g, '').split('/');
			const redirectUrlPath = redirectUrl.pathname.replace(/^\/|\/$/g, '').split('/');
			const ignore = this.redirectOptions.ignoreSegmentOverridePlaceholder || '';
			let newSegments;
			// If the current Path is shorter than the config then the config segments can be added to the end and ignoring segments that where marked
			if (currentPath.length <= redirectUrlPath.length) {
				// Check if the config is the same length or longer as the current url then add the trailing slash in config if present
				shouldAddTrailingSlash = redirectUrlSegements[redirectUrlSegements.length - 1] === '';

				newSegments = redirectUrlSegements
					.map((newSegment, idx) => {
						const oldSegment = currentUrlSegments[idx];
						if (!isNullOrEmpty(newSegment) && newSegment !== ignore) {
							return newSegment;
						}
						return oldSegment;
					})
					.filter((segment) => !isNullOrEmpty(segment));
			} else {
				// If current url is longer add trailing slash if present in current url
				shouldAddTrailingSlash = currentUrlSegments[currentUrlSegments.length - 1] === '';

				newSegments = currentUrlSegments
					.map((oldSegment, idx) => {
						const newSegment = redirectUrlSegements[idx];
						if (!isNullOrEmpty(newSegment) && newSegment !== ignore) {
							return newSegment;
						}
						return oldSegment;
					})
					.filter((segment) => !isNullOrEmpty(segment));
			}
			redirectPathSegments.push(...newSegments);
		} else {
			// prepend path with origin path
			if (this.redirectOptions.preserveOriginPath && !this.originPathPlaceholder) {
				redirectPathSegments.push(...currentUrlSegments);
			}
		}

		// append dynamic pathname
		if (isArrayLike(this.redirectOptions.urlParams) && this.redirectOptions.urlParams.length > 0) {
			const dynamicPathSegments = this.replacePlaceholdersOnCollection(this.redirectOptions.urlParams);
			redirectPathSegments.push(...this.applyParamCaseToCollection(dynamicPathSegments, paramCaseOptions));
		} else if (!this.redirectOptions.preserveOriginPathWithOverrides) {
			// apply pathname derived from static redirect pathname
			redirectPathSegments.push(...redirectUrlSegements);
		}

		// finally append dynamic endpoint
		if (
			this.redirectOptions.urlEndpoint &&
			isArrayLike(this.redirectOptions.urlEndpoint) &&
			this.redirectOptions.urlEndpoint.length > 0
		) {
			const endpointSegments: string[] = this.replacePlaceholdersOnCollection(this.redirectOptions.urlEndpoint);
			const endpoint = this.applyParamCaseToCollection(endpointSegments, paramCaseOptions).join(
				paramCaseOptions.separator,
			);
			redirectPathSegments.push(endpoint);
		}

		// merge pathname segments to save final pathname
		const reducedPathSegments = redirectPathSegments.reduce((segments: string[], segment: string) => {
			if (segment !== '') {
				segments.push(segment);
			}
			return segments;
		}, []);

		if (shouldAddTrailingSlash && reducedPathSegments[reducedPathSegments.length - 1] !== '') {
			reducedPathSegments.push('');
		}
		// save
		redirectUrl.pathname = reducedPathSegments.join('/');
	}

	private updateSearchQueryString(redirectUrl: URL): void {
		let redirectSearchParamsSegments = this.getSearchParamsSegments(redirectUrl);

		// override origin with static redirect search params
		if (this.redirectOptions.preserveOriginQueryString) {
			const originSearchParams = this.getSearchParamsSegments(this.url);
			redirectSearchParamsSegments = Object.assign({}, originSearchParams, redirectSearchParamsSegments);
		}

		// override static redirect with dynamic search params
		if (isArrayLike(this.redirectOptions.queryStringParams) && this.redirectOptions.queryStringParams.length > 0) {
			const dynamicSearchParams = this.redirectOptions.queryStringParams.reduce((params, param) => {
				const replacedValue = this.replacePlaceholders(param.value);
				if (!param.optional || (param.optional && !isNullOrEmpty(replacedValue))) {
					params[param.key] = replacedValue;
				}
				return params;
			}, {});
			redirectSearchParamsSegments = Object.assign({}, redirectSearchParamsSegments, dynamicSearchParams);
		}

		// save search params segments
		const searchParamString = this.getSearchParamsString(redirectSearchParamsSegments);
		if (searchParamString) {
			redirectUrl.search = searchParamString;
		}
	}

	private updateHash(redirectUrl: URL): void {
		if (!redirectUrl.hash && this.redirectOptions.preserveOriginHash) {
			redirectUrl.hash = this.url.hash;
		}
	}

	private applyParamCaseToCollection(collection: string[], options: IRedirectParamCase): string[] {
		if (options.apply) {
			return collection.map((value) => paramCase(value, { delimiter: options.separator }));
		}
		return collection;
	}

	private replacePlaceholdersOnCollection(collection: string[]): string[] {
		let updatedCollection = collection;
		if (this.hasInputs) {
			updatedCollection = updatedCollection.map((value) => this.replacePlaceholders(value));
		}
		return filterOutEmpty(updatedCollection);
	}

	private replacePlaceholders(subject: string): string {
		this.originPathPlaceholder = subject.includes('{originPath}');
		this.inputs['originPath'] = this.originPath;
		return replacePlaceholders({ values: this.inputs, text: subject });
	}

	private getSearchParamsString(searchParams: { [key: string]: string | undefined }): string {
		const paramKeys = Object.keys(searchParams);
		if (paramKeys.length > 0) {
			const params = paramKeys.map((key) => {
				let value = searchParams[key];
				const param = (this.redirectOptions.queryStringParams || []).find((param) => param.key === key);
				const { encode } = param || {};
				if ((this.redirectOptions.encodeQueryStringParams || encode) && encode) {
					value = encodeURIComponent(value);
				}
				return !isNullOrEmpty(searchParams[key]) ? `${key}=${value}` : key;
			});
			return `?${params.join('&')}`;
		}
		return;
	}

	private getSearchParamsSegments(url: URL): { [key: string]: string | undefined } {
		let params = {};
		if (url && url.search) {
			const questionMarkStripped = url.search.replace('?', '');
			if (questionMarkStripped) {
				const paramSegments = questionMarkStripped.split('&');
				if (paramSegments) {
					params = paramSegments.reduce((params, param) => {
						const paramParts = param.split('=');
						if (paramParts[0]) {
							params[paramParts[0]] = (paramParts[1] && decodeURI(paramParts[1])) || undefined;
						}
						return params;
					}, {});
				}
			}
		}
		return params;
	}
}
